import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAssistantResponseStream, fetchThreadMessages, createNewThread } from '../services/openai';

export const useAssistant = () => {
  const [question, setQuestion] = useState('');
  const [error, setError] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const chatEndRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const lastContentRef = useRef('');

  const isSafariOnMac = () => {
    const ua = navigator.userAgent;
    return ua.includes('Safari') && !ua.includes('Chrome') && ua.includes('Macintosh');
  };

  const scrollToBottom = useCallback(() => {
    if (chatEndRef.current) {
      const behavior = isSafariOnMac() ? 'auto' : 'smooth';
      chatEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const fetchChatHistory = useCallback(async (threadId) => {
    try {
      const messages = await fetchThreadMessages(threadId);
      setChatHistory(messages);
      scrollToBottom();
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    const threadId = localStorage.getItem('threadId');
    if (threadId) {
      fetchChatHistory(threadId);
    }
  }, [fetchChatHistory]);

  useEffect(() => {
    if (!isUserScrolling) {
      scrollToBottom();
    }
  }, [chatHistory, isUserScrolling, scrollToBottom]);

  const handleUserScroll = () => {
    if (chatEndRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatEndRef.current.parentNode;
      setIsUserScrolling(scrollHeight - scrollTop !== clientHeight);
    }
  };

  const handleStreamUpdate = useCallback((data) => {
    if (data.status === 'streaming' || data.status === 'completed') {
      setIsTyping(false);
      
      setChatHistory(prev => {
        const newHistory = [...prev];
        const lastMessage = newHistory[newHistory.length - 1];
        
        if (!lastMessage || lastMessage.role !== 'assistant') {
          newHistory.push({ role: 'assistant', content: data.content });
        } else {
          lastMessage.content = data.content;
        }
        
        return newHistory;
      });

      scrollToBottom();
    }
  }, [scrollToBottom]);

  const processMessage = async (message) => {
    if (isProcessing) {
      setError('Prosim počakajte, da se trenutno sporočilo zaključi.');
      return;
    }

    setError('');
    setIsProcessing(true);
    setQuestion('');
    lastContentRef.current = '';

    try {
      setChatHistory(prev => [...prev, { role: 'user', content: message }]);
      scrollToBottom();

      setIsTyping(true);

      const threadId = localStorage.getItem('threadId');
      if (!threadId) {
        await createNewThread(message, handleStreamUpdate);
      } else {
        await fetchAssistantResponseStream(message, threadId, handleStreamUpdate);
      }

    } catch (err) {
      console.error('Error:', err);
      setError('Prišlo je do napake pri komunikaciji. Prosim poskusite ponovno.');
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setIsProcessing(false);
      setIsTyping(false);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!question.trim()) {
      return;
    }
    await processMessage(question);
  };

  const handleRecommendationSubmit = async (rec) => {
    await processMessage(rec);
  };

  const resetChat = async () => {
    localStorage.removeItem('threadId');
    setChatHistory([]);
    setQuestion('');
    setError('');
    setIsProcessing(false);
    setIsTyping(false);
    lastContentRef.current = '';
  };

  return {
    question,
    setQuestion,
    error,
    setError,
    chatHistory,
    setChatHistory,
    isProcessing,
    isTyping,
    setIsTyping,
    handleFormSubmit,
    handleRecommendationSubmit,
    chatEndRef,
    handleUserScroll,
    resetChat,
    processMessage
  };
};
